/**
 * 宛名ラベル(atena)の純関数・定数群。
 * 文字数上限は AtenaPreview のセル寸法から算出した値で、編集側(AtenaEdit)の
 * 警告表示とプレビュー側の赤字表示が同じ値を共有する。
 */

export type AtenaCharLimits = {
  address: number
  companyName: number
  departmentName: number
  staffName: number
}

// 全角1文字 ≈ フォントサイズ分の幅 で各セル列幅から算出した上限
// L判(127×89mm): 列内文字幅 ~54mm → 7pt(2.47mm)で22字/行、住所2行分。氏名は11pt+字間で~12字
// 縦（A4）: 列内幅 ~95mm → 12pt(4.23mm)で22字/行、住所2行分
export const ATENA_CHAR_LIMITS: {
  lsize: AtenaCharLimits
  portrait: AtenaCharLimits
} = {
  lsize: { address: 44, companyName: 22, departmentName: 22, staffName: 12 },
  portrait: { address: 44, companyName: 22, departmentName: 22, staffName: 22 }
}

// 宛名の最大入力件数。L判はシール紙1枚(宛先2面+差出人2面)のため2件まで
export const ATENA_MAX_ITEMS = { lsize: 2, portrait: 8 }

export type AtenaItem = {
  postNumber: string
  address: string
  companyName: string
  departmentName: string
  staffName: string
  uuid: string
}

export type AtenaFormatData = {
  createDate: Date | string | null
  lastName: string
  firstName: string
  postNumber: string
  address1: string
  address2: string
  atenaItems: AtenaItem[]
  selectedPaper: string
} & Record<string, unknown>

function uuidv4(): string {
  // '10000000-1000-4000-8000-100000000000' の各桁を乱数で置換する crypto ベースの UUID v4
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (window.crypto.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

export function createAtenaItem(columns: Partial<AtenaItem> = {}): AtenaItem {
  return {
    postNumber: columns.postNumber || '',
    address: columns.address || '',
    companyName: columns.companyName || '',
    departmentName: columns.departmentName || '',
    staffName: columns.staffName || '',
    uuid: uuidv4()
  }
}

// 保存データの Object.assign 先。ここに無いキーは Vue 2.7 ではリアクティブに
// ならないため、UI が読み書きするキーは必ず全て宣言しておく
export function createEmptyAtenaFormatData(): AtenaFormatData {
  return {
    createDate: null,
    lastName: '',
    firstName: '',
    postNumber: '',
    address1: '',
    address2: '',
    atenaItems: [],
    selectedPaper: 'l-size'
  }
}

/**
 * ロード済みデータの正規化。
 * - atenaItems 欠落・null → []
 * - selectedPaper 未設定 → 'l-size' / 廃止したB5 → 縦の代替(A4)
 */
export function normalizeAtenaFormatData(data: AtenaFormatData): void {
  if (!Array.isArray(data.atenaItems)) {
    data.atenaItems = []
  }
  if (!data.selectedPaper) {
    data.selectedPaper = 'l-size'
  }
  if (data.selectedPaper === 'b5-size') {
    data.selectedPaper = 'a4-size'
  }
}

export function isAtenaLSize(
  selectedPaper: string | undefined | null
): boolean {
  return !selectedPaper || selectedPaper === 'l-size'
}
